import React, { useState, useEffect } from 'react';
import { X, TrendingUp, AlertCircle, Calculator, Link, Unlink } from 'lucide-react';
import { pricingService } from '../../services/pricingService';
import './PriceIncreaseModal.css';
import { useTrackModal } from '../../hooks/useTrackModal';

const PriceIncreaseModal = ({ onClose, onApply }) => {
  useTrackModal("PriceIncreaseModal");
  const [applyToAll, setApplyToAll] = useState(false);
  const [masterPercentage, setMasterPercentage] = useState('');
  const [adjustments, setAdjustments] = useState({
    tiers: '', // percentage
    linework: '', // dollar amount
    erosion: '', // dollar amount
    advUtility: '', // dollar amount per structure
    utilityPercent: '' // percentage (defaults to current 10%)
  });
  const [reason, setReason] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().split('T')[0]);
  const [compound, setCompound] = useState(true);
  const [applying, setApplying] = useState(false);
  const [currentValues, setCurrentValues] = useState(null);
  const [previewResults, setPreviewResults] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load current values on mount
  useEffect(() => {
    loadCurrentValues();
  }, []);

  const loadCurrentValues = async () => {
    try {
      console.log('PriceIncreaseModal: Starting to load current values...');
      setLoading(true);
      const [tiers, adjustments] = await Promise.all([
        pricingService.getPricingTiers(),
        pricingService.getPricingAdjustments()
      ]);
      console.log('PriceIncreaseModal: Loaded pricing data successfully');

      // Extract current values
      const linework = adjustments.find(a => a.name === 'Linework');
      const erosion = adjustments.find(a => a.name === 'Erosion Surface');
      const advUtility = adjustments.find(a => a.name === 'Advanced Utilities');
      const utilityPercent = adjustments.find(a => a.name === 'Utilities');

      const currentLinework = linework?.current_value || 450;
      const currentErosion = erosion?.current_value || 450;
      const currentAdvUtility = advUtility?.current_value || 30;
      const currentUtilityPercent = utilityPercent?.current_value || 10;

      setCurrentValues({
        tiers: tiers,
        linework: currentLinework,
        erosion: currentErosion,
        advUtility: currentAdvUtility,
        utilityPercent: currentUtilityPercent
      });

      // Set default values - current prices for fixed amounts, current percentage for utilities
      setAdjustments({
        tiers: '',
        linework: currentLinework.toString(),
        erosion: currentErosion.toString(),
        advUtility: currentAdvUtility.toString(),
        utilityPercent: currentUtilityPercent.toString()
      });
    } catch (error) {
      console.error('PriceIncreaseModal: Error loading current values:', error);
    } finally {
      console.log('PriceIncreaseModal: Setting loading to false');
      setLoading(false);
    }
  };

  const handleMasterPercentageChange = (value) => {
    setMasterPercentage(value);
    if (applyToAll && currentValues) {
      // Apply percentage to tier pricing only - other items keep their individual logic
      setAdjustments({
        tiers: value,
        linework: currentValues.linework.toString(), // Keep current value
        erosion: currentValues.erosion.toString(), // Keep current value  
        advUtility: currentValues.advUtility.toString(), // Keep current value
        utilityPercent: currentValues.utilityPercent.toString() // Keep current percentage
      });
    }
  };

  const handleAdjustmentChange = (field, value) => {
    setAdjustments({
      ...adjustments,
      [field]: value
    });
  };

  const toggleApplyToAll = () => {
    const newApplyToAll = !applyToAll;
    setApplyToAll(newApplyToAll);
    
    if (newApplyToAll && masterPercentage && currentValues) {
      // Apply percentage only to tiers, keep others at current values
      handleMasterPercentageChange(masterPercentage);
    }
  };

  const calculatePreview = () => {
    if (!currentValues) return;

    const results = [];

    // Only show tier examples if acreage-based percentage increase is specified
    if (adjustments.tiers && parseFloat(adjustments.tiers) !== 0) {
      const tierPercent = parseFloat(adjustments.tiers);
      
      // Small project example (0.5 acres)
      results.push({
        name: 'Small Project (0.5 acres)',
        current: 1200,
        new: Math.ceil((1200 * (1 + tierPercent / 100)) / 10) * 10,
        change: `+${tierPercent}%`
      });

      // Medium project example (3 acres)
      const mediumCurrent = 1220 + (280 * 3);
      results.push({
        name: 'Medium Project (3 acres)',
        current: mediumCurrent,
        new: Math.ceil((mediumCurrent * (1 + tierPercent / 100)) / 10) * 10,
        change: `+${tierPercent}%`
      });

      // Large project example (50 acres)
      const largeCurrent = 1350 * Math.pow(50, 0.38) + 141.47;
      const largeCurrentRounded = Math.ceil(largeCurrent / 10) * 10;
      results.push({
        name: 'Large Project (50 acres)',
        current: largeCurrentRounded,
        new: Math.ceil((largeCurrent * (1 + tierPercent / 100)) / 10) * 10,
        change: `+${tierPercent}%`
      });
    }

    // Only show items that will actually change
    const newLinework = adjustments.linework ? parseFloat(adjustments.linework) : currentValues.linework;
    if (newLinework !== currentValues.linework) {
      results.push({
        name: 'Linework Only',
        current: currentValues.linework,
        new: newLinework,
        change: `$${(newLinework - currentValues.linework).toLocaleString()}`
      });
    }

    const newErosion = adjustments.erosion ? parseFloat(adjustments.erosion) : currentValues.erosion;
    if (newErosion !== currentValues.erosion) {
      results.push({
        name: 'Erosion Surface',
        current: currentValues.erosion,
        new: newErosion,
        change: `$${(newErosion - currentValues.erosion).toLocaleString()}`
      });
    }

    const newAdvUtility = adjustments.advUtility ? parseFloat(adjustments.advUtility) : currentValues.advUtility;
    if (newAdvUtility !== currentValues.advUtility) {
      results.push({
        name: 'Advanced Utilities (per structure)',
        current: currentValues.advUtility,
        new: newAdvUtility,
        change: `$${(newAdvUtility - currentValues.advUtility).toLocaleString()}`
      });
    }

    // Only show utility percentage if it will change
    const newUtilityPercent = adjustments.utilityPercent ? parseFloat(adjustments.utilityPercent) : currentValues.utilityPercent;
    if (newUtilityPercent !== currentValues.utilityPercent) {
      results.push({
        name: 'Utilities Percentage',
        current: currentValues.utilityPercent + '%',
        new: newUtilityPercent + '%',
        change: `${newUtilityPercent > currentValues.utilityPercent ? '+' : ''}${(newUtilityPercent - currentValues.utilityPercent).toFixed(1)} points`,
        isPercentageAdjustment: true
      });
    }

    setPreviewResults(results);
  };

  const handleApply = async () => {
    console.log('🚀 PriceIncreaseModal: Starting handleApply');
    console.log('📝 Form values:', {
      reason: reason.trim(),
      adjustments,
      compound,
      effectiveDate
    });

    if (!reason.trim()) {
      console.error('❌ No reason provided');
      alert('Please provide a reason for this price increase');
      return;
    }

    // Check if any values will actually change from current values
    const hasActualChanges = (
      (adjustments.tiers && parseFloat(adjustments.tiers) !== 0) ||
      (adjustments.linework && parseFloat(adjustments.linework) !== currentValues.linework) ||
      (adjustments.erosion && parseFloat(adjustments.erosion) !== currentValues.erosion) ||
      (adjustments.advUtility && parseFloat(adjustments.advUtility) !== currentValues.advUtility) ||
      (adjustments.utilityPercent && parseFloat(adjustments.utilityPercent) !== currentValues.utilityPercent)
    );
    
    console.log('🔍 Has actual changes check:', hasActualChanges, 'Current values:', currentValues, 'Adjustments:', adjustments);
    
    if (!hasActualChanges) {
      console.error('❌ No actual changes detected');
      alert('Please make at least one change from the current values');
      return;
    }

    try {
      console.log('⏳ Setting applying state to true');
      setApplying(true);

      // Create adjustment data for the selective pricing structure
      const adjustmentData = {
        tiers: adjustments.tiers ? parseFloat(adjustments.tiers) : null,
        linework: adjustments.linework ? parseFloat(adjustments.linework) : null,
        erosion: adjustments.erosion ? parseFloat(adjustments.erosion) : null,
        advUtility: adjustments.advUtility ? parseFloat(adjustments.advUtility) : null,
        utilityPercent: adjustments.utilityPercent ? parseFloat(adjustments.utilityPercent) : null,
        compound,
        reason,
        effectiveDate
      };

      console.log('📊 Adjustment data prepared:', adjustmentData);
      
      console.log('🎯 Calling pricingService.applySelectivePriceAdjustments with:', adjustmentData);
      
      const result = await pricingService.applySelectivePriceAdjustments(adjustmentData);

      console.log('✅ Service call successful, result:', result);
      console.log('🎉 Calling onApply callback');
      onApply();
    } catch (error) {
      console.error('❌ Error applying increase - Full error object:', error);
      console.error('❌ Error message:', error?.message);
      console.error('❌ Error stack:', error?.stack);
      
      // Show more specific error message
      const errorMessage = error?.message || 'Unknown error occurred';
      alert(`Failed to apply price increase: ${errorMessage}`);
    } finally {
      console.log('🏁 Setting applying state to false');
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="price-increase-modal enhanced">
          <div className="modal-header">
            <h2>
              <TrendingUp size={24} />
              Apply Price Adjustments
            </h2>
            <button className="close-button" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
          <div className="modal-body">Loading current prices...</div>
          <div className="modal-footer">
            <button className="cancel-button" onClick={onClose}>
              Cancel
            </button>
            <button className="apply-button" disabled>
              Loading...
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="price-increase-modal enhanced">
        <div className="modal-header">
          <h2>
            <TrendingUp size={24} />
            Apply Price Adjustments
          </h2>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          <div className="increase-info">
            <AlertCircle size={18} />
            <span>Apply percentage increases to pricing elements. You can apply the same percentage to all, or set individual percentages.</span>
          </div>

          <div className="form-section">
            <div className="form-group">
              <label>Effective Date</label>
              <input
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
              />
              <div className="field-help">
                You can backdate changes by selecting a past date.
              </div>
            </div>

            <div className="apply-to-all-section">
              <label className="checkbox-label featured">
                <input
                  type="checkbox"
                  checked={applyToAll}
                  onChange={toggleApplyToAll}
                />
                {applyToAll ? <Link size={18} /> : <Unlink size={18} />}
                <span>Apply same percentage to all items</span>
              </label>
            </div>

            {applyToAll ? (
              <div className="form-group">
                <label>Percentage Increase for Tiers Only (%)</label>
                <input
                  type="number"
                  value={masterPercentage}
                  onChange={(e) => handleMasterPercentageChange(e.target.value)}
                  placeholder="e.g., 5"
                  step="0.1"
                />
                <div className="field-help">
                  This percentage will only apply to acreage-based tiers. Other items remain at current values.
                </div>
              </div>
            ) : (
              <div className="individual-adjustments">
                <h4>Individual Price Adjustments</h4>
                
                <div className="adjustments-grid">
                  <div className="form-group">
                    <label>Size-Based Tiers (%)</label>
                    <input
                      type="number"
                      value={adjustments.tiers}
                      onChange={(e) => handleAdjustmentChange('tiers', e.target.value)}
                      placeholder="0"
                      step="0.1"
                    />
                    <span className="current-value">Affects all acreage formulas</span>
                  </div>

                  <div className="form-group">
                    <label>Linework Only ($)</label>
                    <input
                      type="number"
                      value={adjustments.linework}
                      onChange={(e) => handleAdjustmentChange('linework', e.target.value)}
                      placeholder={currentValues?.linework || '450'}
                      step="10"
                    />
                    <span className="current-value">Current: ${currentValues?.linework || '450'}</span>
                    <span className="field-help">Enter new total price (not percentage)</span>
                  </div>

                  <div className="form-group">
                    <label>Erosion Surface ($)</label>
                    <input
                      type="number"
                      value={adjustments.erosion}
                      onChange={(e) => handleAdjustmentChange('erosion', e.target.value)}
                      placeholder={currentValues?.erosion || '450'}
                      step="10"
                    />
                    <span className="current-value">Current: ${currentValues?.erosion || '450'}</span>
                    <span className="field-help">Enter new total price (not percentage)</span>
                  </div>

                  <div className="form-group">
                    <label>Advanced Utilities ($ per structure)</label>
                    <input
                      type="number"
                      value={adjustments.advUtility}
                      onChange={(e) => handleAdjustmentChange('advUtility', e.target.value)}
                      placeholder={currentValues?.advUtility || '30'}
                      step="5"
                    />
                    <span className="current-value">Current: ${currentValues?.advUtility || '30'}/structure</span>
                    <span className="field-help">Enter new price per structure (not percentage)</span>
                  </div>

                  <div className="form-group">
                    <label>Utilities Percentage (%)</label>
                    <input
                      type="number"
                      value={adjustments.utilityPercent}
                      onChange={(e) => handleAdjustmentChange('utilityPercent', e.target.value)}
                      placeholder={currentValues?.utilityPercent || '10'}
                      step="0.5"
                      min="0"
                      max="100"
                    />
                    <span className="current-value">Current: {currentValues?.utilityPercent || '10'}% of base</span>
                    <span className="field-help">Enter new percentage (e.g., 12 for 12% of base)</span>
                  </div>
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={compound}
                  onChange={(e) => setCompound(e.target.checked)}
                />
                <span>Compound on previous increases</span>
              </label>
              <div className="field-help">
                When enabled, the increase will be applied to the current price (which may include previous increases).
              </div>
            </div>

            <div className="form-group">
              <label>Reason for Increase <span className="required">*</span></label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Annual price adjustment, Inflation adjustment, Market conditions..."
                rows="3"
              />
            </div>

            <button 
              className="preview-button full-width"
              onClick={calculatePreview}
              disabled={!Object.values(adjustments).some(p => p && parseFloat(p) !== 0)}
            >
              <Calculator size={16} />
              Preview Changes
            </button>
          </div>

          {previewResults && (
            <div className="preview-section">
              <h3>Preview of Changes</h3>
              <div className="preview-table">
                <div className="preview-header">
                  <span>Item</span>
                  <span>Current</span>
                  <span>New</span>
                  <span>Change</span>
                </div>
                {previewResults.map((item, index) => (
                  <div key={index} className="preview-row">
                    <span className="item-name">{item.name}</span>
                    <span className="current-value">
                      {item.isPercentageAdjustment ? item.current : `$${item.current.toLocaleString()}`}
                    </span>
                    <span className="new-value">
                      {item.isPercentageAdjustment ? item.new : `$${item.new.toLocaleString()}`}
                    </span>
                    <span className="change-value">
                      {item.change}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="cancel-button" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="apply-button"
            onClick={handleApply}
            disabled={applying || !reason.trim()}
          >
            {applying ? 'Applying...' : 'Apply Increases'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PriceIncreaseModal;